//page where there is a collectio of styles and user can select multiple of them and a total price is calculated

import React, { useEffect, useState } from 'react';
import HumanReadableIds from 'human-readable-ids';
import WomanIcon from '@mui/icons-material/Woman';
import ManIcon from '@mui/icons-material/Man';
import PetsIcon from '@mui/icons-material/Pets';
import { FaCat, FaDog } from 'react-icons/fa';

import { Style } from '../../models/Style';
import { useRouter } from 'next/router';
import styleService from '../../services/styleService';
import StyleCard from '../../components/style-card/StyleCard';
import { Plan } from '../../models/Plan';
import { useAuth } from '../../context/AuthUserContext';
import planService from '../../services/planService';
import modelService from '../../services/modelService';
import { useDispatch } from 'react-redux';
import { customTextModalShow } from '../../redux/counterSlice';
import userService from '../../services/userService';
import { FlowSteps } from '../../models/FlowSteps';
import { DatabaseUser } from '../../models/User';
import LoadingSpinner from '../../components/LoadingSpinner';
import { classes } from '../../models/Model';
import Lightbox from 'react-image-lightbox';

export type Category = {
  val: string;
  text?: string;
};

const StylesSelection = () => {
  const [selectedStyles, setSelectedStyles] = useState<Partial<Style>[]>([]);
  const [allStyles, setAllStyles] = useState<Partial<Style>[]>([]);
  const [databaseUser, setDatabaseUser] = useState<DatabaseUser>(null);
  const [category, setCategory] = useState<Category>({ val: 'Man', text: 'Hombre' });
  const [selectedStylePreview, setSelectedStylePreview] = useState<Style>(null);
  const [lightBoxOpen, setLightBoxOpen] = useState<boolean>(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number>(null);
  const basePrice = 0.99;
  const categoryList = [
    { val: 'Man', text: 'Hombre' },
    { val: 'Woman', text: 'Mujer' },
    { val: 'Dog', text: 'Perro' },
    { val: 'Cat', text: 'Gato' },
  ];
  const router = useRouter();
  const { getAllStyles } = styleService();
  const { createPlan, getPlan, updatePlan } = planService();
  const { createModel, updateModel } = modelService();
  const { updateDatabaseUser, getDatabaseUser } = userService();

  const dispatch = useDispatch();

  const { authUser, loading } = useAuth();
  const [isLoading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState('Cargando...');

  // Listen for changes on loading and authUser, redirect if needed
  useEffect(() => {
    if (!loading && !authUser) {
      console.log('redirecting to login', authUser);
      router.push('/login');
    }
    if (!loading && authUser) {
      console.log('user is logged in', authUser);
      checkUserCurrentStep();
    }
  }, [authUser, loading]);

  useEffect(() => {
    console.log('fetching styles');
    fetchStyles();
  }, []);

  const handleFilterClick = (filter: Category) => {
    setCategory(filter);
    setSelectedStyles([]);
  };

  const checkUserCurrentStep = async () => {
    const user = await getDatabaseUser(authUser.uid);
    if (!user) router.push('/login');
    setDatabaseUser(user);

    if (user?.currentPlanId?.length > 0) {
      const currentPlan = await getPlan(user.currentPlanId);
      if (!currentPlan) {
        setNewPlan();
        return;
      }
      switch (currentPlan.step) {
        case FlowSteps.SelectPlan:
          //TODO: set Category to current class plan value
          currentPlan.styles.forEach((style) => {
            if (!style.default) {
              console.log('Agregando estilo', style);
              handleStyleSelection(style);
            }
          });
          break;
        case FlowSteps.UploadPictures:
          //TODO: set Category to current class plan value
          currentPlan.styles.forEach((style) => {
            if (!style.default) {
              console.log('Agregando estilo', style);
              handleStyleSelection(style);
            }
          });
          break;
        case FlowSteps.WaitForProcessing:
          // router.push('/all-plans/');
          break;
        case FlowSteps.ProcessingReady:
          // router.push('/all-plans/');
          break;
        default:
          break;
      }
    } else {
      setNewPlan();
    }
  };

  const setNewPlan = async () => {
    const createdModel = await createModel('Man', authUser.uid);
    const humanReadableId = (HumanReadableIds.hri.random() as string).replaceAll('-', '');
    const plan: Plan = {
      generatedPictures: [],
      userId: authUser.uid,
      humanReadableId,
      class: 'Man',
      modelId: createdModel.id,
      styles: [],
      price: getTotalPrice,
      createdAt: new Date() as any,
      step: 2,
      paid: false,
      status: 'pending',
      replicateVersions: [],
    };
    const createdPlan = await createPlan(plan);
    await updateDatabaseUser(authUser.uid, { currentPlanId: createdPlan.id });
    setDatabaseUser({ ...databaseUser, currentPlanId: createdPlan.id });
  };

  const fetchStyles = async () => {
    try {
      setLoadingText('Cargando estilos...');
      setLoading(true);
      const styles = await getAllStyles();

      setAllStyles(styles);
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  const getTotalPrice = parseFloat(
    (selectedStyles.reduce((total, item) => item.price + total, 0) + basePrice).toFixed(2) //Promo price
  );
  const getOriginalTotalPrice = parseFloat(
    ((selectedStyles.reduce((total, item) => item.price + total, 0) + basePrice) * 1.5).toFixed(0) //Original price
  );
  const defaultStyles = [
    ...allStyles.filter((style: Style) => !!style.default && style.class === category.val), //Default styles
  ];
  const extraStyles = [
    ...allStyles.filter((style: Style) => !style.default && style.class === category.val), //Extra styles
  ];

  const handleStyleSelection = (style: Partial<Style>) => {
    if (selectedStyles.some((selectedStyle) => selectedStyle.id === style.id)) {
      setSelectedStyles(selectedStyles.filter((s) => s.id !== style.id));
    } else {
      setSelectedStyles([...selectedStyles, style]);
    }
  };

  const continueToUploadImages = async () => {
    try {
      console.log('continueToUploadImages', selectedStyles, defaultStyles, getTotalPrice);
      await updatePlan(databaseUser.currentPlanId, {
        class: category.val as classes,
        step: 3,
        styles: [...selectedStyles, ...defaultStyles],
        price: getTotalPrice,
      });
      const modelId = (await getPlan(databaseUser.currentPlanId)).modelId;
      await updateModel(modelId, {
        class: category.val as classes,
      });
      router.push('/upload');
    } catch (error) {
      console.error(error);
      dispatch(
        customTextModalShow([
          'Ocurrió un error al actualizar la base de datos, por favor intenta de nuevo o contáctanos al correo: soporte@mifotodeperfil.com',
          error,
          'Ok',
        ])
      );
    }
  };

  const renderSwitchIcon = (param) => {
    switch (param) {
      case 'Woman':
        return <WomanIcon />;
      case 'Man':
        return <ManIcon />;
      case 'Dog':
        return <FaDog className='scale-110 mr-2' />;
      case 'Cat':
        return <FaCat className='scale-110 mr-2' />;
      default:
        return <></>;
    }
  };

  const openImageLightBox = (style: Style) => {
    setSelectedStylePreview(style);
    setLightBoxOpen(true);
  };

  const onCloseImageLightBox = () => {
    setSelectedPhotoIndex(0);
    setLightBoxOpen(false);
  };

  return (
    <>
      {isLoading && <LoadingSpinner loadingText={loadingText} />}
      <section className='relative flex flex-col justify-center py-4 md:py-24 items-center'>
        {/* Syles title */}
        <div className='max-w-7xl mx-auto px-4 py-2 sm:px-6 lg:px-8'>
          <div className='text-center'>
            <p className='md:mt-2 text-3xl font-extrabold text-gray-900 dark:text-white sm:text-4xl'>
              Selecciona mínimo 5 estilos de imágenes para continuar
            </p>
            <p className='mt-2 text-gray-900 dark:text-white'>
              Obtendrás 16 imágenes por cada estilo que elijas. Cada uno tiene un costo de $1 USD.
            </p>
            {/* percentage of promo text */}
            <p className='mt-2 text-gray-900 dark:text-white text-lg '>
              <span className='font-bold green'>¡Aprovecha nuestra promoción de lanzamiento! </span>{' '}
              <br></br>
              <span className='line-through'> ${getOriginalTotalPrice}.99 USD </span> <br></br>
              <span className='green font-bold'> ${getTotalPrice} USD</span>
            </p>
          </div>
        </div>
        <div className='md:mb-8 flex flex-wrap items-center justify-between'>
          <ul className='flex flex-wrap items-center justify-center'>
            {categoryList.map(({ val, text }) => {
              return (
                <li className='my-1 mr-2.5' key={val}>
                  <button
                    onClick={() => {
                      handleFilterClick({ val, text });
                    }}
                  >
                    <div
                      className={
                        category.val === val
                          ? 'dark:border-jacarta-600 group bg-accent border-jacarta-100 font-display flex h-9 items-center rounded-lg border px-4 text-sm font-semibold transition-colors border-transparent text-white dark:border-transparent capitalize'
                          : 'dark:border-jacarta-600 dark:bg-jacarta-900 dark:hover:bg-accent group hover:bg-accent border-jacarta-100 font-display text-jacarta-500 flex h-9 items-center rounded-lg border bg-white px-4 text-sm font-semibold transition-colors hover:border-transparent hover:text-white dark:text-white dark:hover:border-transparent dark:hover:text-white capitalize'
                      }
                    >
                      {renderSwitchIcon(val)}
                      <span>{text}</span>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
          {/* dropdown */}
        </div>

        <div className='flex flex-wrap justify-center dark:border-jacarta-600 border-jacarta-100 rounded border-solid max-w-7xl mx-2'>
          {/* <fieldset className='border-solid border-purple border-2 rounded-xl width-auto flex flex-wrap justify-center relative px-2'>
            <legend className='text-center text-gray-900 dark:text-white opacity-80 font-semibold'>
              Paquete inicial
            </legend>
            <legend className='text-gray-900 dark:text-white mx-1 absolute my-top-right-legened dark:bg-jacarta-900 bg-white'>
              {defaultStyles?.length}/{defaultStyles?.length}
            </legend>
            {defaultStyles.map((style: Style) =>
              style ? (
                <StyleCard
                  key={style.id + style.name}
                  style={style}
                  selectedStyles={selectedStyles}
                  handleStyleSelection={handleStyleSelection}
                  openPreview={openImageLightBox}
                />
              ) : (
                <> </>
              )
            )}
          </fieldset> */}

          <fieldset className='border-solid border-green border-2 rounded-xl width-auto flex flex-wrap justify-center mx-2 px-2 py-4 mt-8 relative'>
            <legend className='text-gray-900 dark:text-white mx-1 absolute my-top-right-legened dark:bg-jacarta-900 bg-white'>
              {selectedStyles?.length}/{extraStyles?.length}
            </legend>
            {extraStyles.map((style: Style) =>
              style ? (
                <StyleCard
                  key={style.id + style.name}
                  style={style}
                  selectedStyles={selectedStyles}
                  handleStyleSelection={handleStyleSelection}
                  openPreview={openImageLightBox}
                />
              ) : (
                <> </>
              )
            )}
          </fieldset>
        </div>
        {selectedStyles.length < 5 && selectedStyles.length >= 1 && (
          <div className='bg-accent fixed bottom-2 shadow-accent-volume rounded-full py-3 px-8 text-center font-semibold text-white transition-all md:m-4 text-sm'>
            Selecciona {5 - selectedStyles.length} estilo(s) más para continuar
          </div>
        )}
        {selectedStyles.length > 4 && (
          <button
            onClick={continueToUploadImages}
            className='bg-accent fixed bottom-2 shadow-accent-volume hover:bg-accent-dark rounded-full py-3 px-8 text-center font-semibold text-white transition-all m-4'
          >
            Continuar con{' '}
            {(allStyles.filter((style) => style.default && style.class === category.val)?.length +
              selectedStyles?.length) *
              16}{' '}
            imágenes
          </button>
        )}
      </section>
      {!!allStyles.length && lightBoxOpen && (
        <Lightbox
          mainSrc={selectedStylePreview.previewUrls[selectedPhotoIndex ?? 0]}
          nextSrc={
            selectedStylePreview.previewUrls[
              (selectedPhotoIndex + 1) % selectedStylePreview.previewUrls.length
            ]
          }
          prevSrc={
            selectedStylePreview.previewUrls[
              (selectedPhotoIndex + selectedStylePreview.previewUrls.length - 1) %
                selectedStylePreview.previewUrls.length
            ]
          }
          onCloseRequest={onCloseImageLightBox}
          onMovePrevRequest={() =>
            setSelectedPhotoIndex(
              (selectedPhotoIndex + selectedStylePreview.previewUrls.length - 1) %
                selectedStylePreview.previewUrls.length
            )
          }
          onMoveNextRequest={() =>
            setSelectedPhotoIndex(
              (selectedPhotoIndex + 1) % selectedStylePreview.previewUrls.length
            )
          }
        />
      )}
    </>
  );
};

export default StylesSelection;
